// ResetPasswordPage.js

import React, { useEffect, useState } from "react";
import "../CSS/LoginForm.css";
import { useParams } from "react-router-dom";
import sensen_Login_Image from "../Assets/sensen_Login_Image.png";
import {
  FormControl,
  IconButton,
  InputAdornment,
  OutlinedInput,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";

const ResetPasswordPage = () => {
  const { custId, name, resetToken } = useParams();
  const [showBackdrop, setShowBackdrop] = useState(false);
  const [showInvalidLinkMessage, setShowInvalidLinkMessage] = useState(false);
  const [authToken, setAuthToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetError, setResetError] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [hasMinimumLength, setHasMinimumLength] = useState(false);
  const [hasLowerCase, setHasLowerCase] = useState(false);
  const [hasUpperCase, setHasUpperCase] = useState(false);
  const [hasNumber, setHasNumber] = useState(false);
  const [count, setCount] = useState(5);
  const resetPasswordAuthentication = async (custId, name, resetToken) => {
    const apiUrl = `/sn-auth-service/${custId}/resetPasswordAuthentication/${name}/${resetToken}`;

    try {
      setShowBackdrop(true);
      setShowInvalidLinkMessage(false);
      const response = await fetch(apiUrl, {
        method: "POST",
        // Add any additional configurations as needed
      });

      if (!response.ok) {
        setShowBackdrop(false);
        setShowInvalidLinkMessage(true);
        throw new Error(
          `Failed to authenticate reset password. Status: ${response.status}`
        );
      }

      const data = await response.json();
      setShowBackdrop(false);
      setAuthToken(data);
      // console.log('Response:', data);
    } catch (error) {
      console.error("Error:", error);
      setShowBackdrop(false);
      setShowInvalidLinkMessage(true);
      throw error;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const responseData = await resetPasswordAuthentication(
          custId,
          name,
          resetToken
        );
        // Handle the response data as needed
      } catch (error) {
        // Handle errors
        console.error("Authentication error:", error);
      }
    };

    // Call the function when the component mounts
    fetchData();
  }, [custId, name, resetToken]);

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;

    // Check password conditions
    setHasMinimumLength(newPassword.length >= 8);
    setHasLowerCase(/[a-z]/.test(newPassword));
    setHasUpperCase(/[A-Z]/.test(newPassword));
    setHasNumber(/\d/.test(newPassword));
    // Update password state
    setPassword(newPassword);
  };

  // Update isPasswordValid whenever individual conditions change
  useEffect(() => {
    setIsPasswordValid(
      hasMinimumLength && hasLowerCase && hasUpperCase && hasNumber
    );
  }, [hasMinimumLength, hasLowerCase, hasUpperCase, hasNumber]);

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent the default form submission
    // Validate passwords
    if (password !== confirmPassword) {
      setPasswordsMatch(false);
      return;
    }
    // Check if both passwords satisfy all conditions
    const isNewPasswordValid =
      password.length >= 8 &&
      /[a-z]/.test(password) &&
      /[A-Z]/.test(password) &&
      /\d/.test(password);

    if (!isNewPasswordValid) {
      // Set error state if passwords do not meet the criteria
      setPasswordsMatch(false);
      return;
    }
    setShowBackdrop(true);
    try {
      // Call the API to reset the password
      const response = await fetch("/sn-auth-service/passwordReset", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          custID: custId,
          username: name,
          plainPassword: password,
          token: authToken,
        }),
      });

      // Check the response status
      if (!response.ok) {
        setShowBackdrop(false);
        throw new Error("Failed to reset password.");
      }
      // Convert response body to JSON
      const responseDataApi = await response.json();
      // console.log("Response data:", responseDataApi);
      setShowBackdrop(false);
      // Reset was successful
      setResetSuccess(true);
      setResetError(null);
      // Set timer to run the function after 4 seconds
      setInterval(() => {
        setCount((count) => count - 1);
      }, 1000);

      setTimeout(() => {
        window.location.href = responseDataApi;
      }, 4000); // 4000 milliseconds = 4 seconds
    } catch (error) {
      // Handle errors
      setShowBackdrop(false);
      console.error("Error resetting password:", error);
      setResetError("Failed to reset password. Please try again.");
    }
  };

  const handleTogglePassword = () => {
    setShowPassword((prevShowConfirmPassword) => !prevShowConfirmPassword);
  };
  const handleToggleConfirmPassword = () => {
    setShowConfirmPassword((prevShowPassword) => !prevShowPassword);
  };

  const handleCopyPaste = (e) => {
    e.preventDefault();
  };

  return (
    <div className="page-container showNormalFont">
      {showBackdrop && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={showBackdrop}
          onClick={() => {}}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      {showInvalidLinkMessage ? (
        <div className="invalid-link-content-container">
          <div className="mb-2">
            <div className="round-box">
              <CloseIcon className="close-icon" />
            </div>
          </div>
          <div className="row invalid-text mb-2">Invalid Link</div>
          <div className="row invalid-paragraph">
            This link has already been used. To reset your <br /> password,
            return to the login page and select <br /> "Forgot Your Password" to
            send a new email.
          </div>
        </div>
      ) : resetSuccess ? (
        <div className="invalid-link-content-container">
          <div className="mb-2">
            <div className="round-box-Check-icon">
              <CheckIcon className="Check-icon" />
            </div>
          </div>
          <div className="row invalid-text mb-2">Password Changed!</div>
          <div className="row invalid-paragraph">
            Your Password has been changed successfully. <br />
            Please wait, you will be redirected to login page in {count} seconds
          </div>
        </div>
      ) : resetError ? (
        <div className="invalid-link-content-container">
          <div className="mb-2">
            <div className="round-box">
              <CloseIcon className="close-icon" />
            </div>
          </div>
          <div className="row invalid-text mb-2">Password Reset Failed</div>
          <div className="row invalid-paragraph">
            Password reset failed. Please try again.
          </div>
        </div>
      ) : (
        <form
          className="content-container-resetpassword"
          onSubmit={handleSubmit}
        >
          <div className="login-header w-100 ">
            <img src={sensen_Login_Image} alt="Logo" className="form-logo" />
            <div className="form-title">
              <h1>Change Your Password</h1>
            </div>
            <h2 className="form-subtitle">
              Enter a new password below to change your password.
            </h2>
          </div>

          <div className="login-body w-100">
            <div className="inputContainer w-100">
              <div className="form-floating w-100">
                <FormControl fullWidth variant="outlined">
                  <p className="mt-2">Password</p>
                  <OutlinedInput
                    id="outlined-password-input"
                    type={showPassword ? "text" : "password"}
                    className={`inputField ${
                      !passwordsMatch ? "inputErrorPassword" : ""
                    }`}
                    onCopy={handleCopyPaste}
                    onPaste={handleCopyPaste}
                    value={password}
                    placeholder="New password"
                    onChange={handlePasswordChange}
                    fullWidth
                    required
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton onClick={handleTogglePassword} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </FormControl>
              </div>
            </div>
            <div className="inputContainer w-100">
              <FormControl fullWidth variant="outlined">
                <p className="mt-2">Confirm Password</p>
                <OutlinedInput
                  id="outlined-confirm-password-input"
                  type={showConfirmPassword ? "text" : "password"}
                  className={`inputField ${
                    !passwordsMatch ? "inputErrorPassword" : ""
                  }`}
                  onCopy={handleCopyPaste}
                  onPaste={handleCopyPaste}
                  value={confirmPassword}
                  placeholder="Re-enter new password"
                  onChange={handleConfirmPasswordChange}
                  fullWidth
                  required
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleToggleConfirmPassword}
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </FormControl>
            </div>
            <div className={`inputContainer w-100`}>
            <p>Your password must contain:</p>
                <ul>
                  <li className={hasMinimumLength ? "valid" : "invalid"}>
                    At least 8 characters
                  </li>
                  <li className={hasLowerCase ? "valid" : "invalid"}>
                    Lower case letters (a-z)
                  </li>
                  <li className={hasUpperCase ? "valid" : "invalid"}>
                    Upper case letters (A-Z)
                  </li>
                  <li className={hasNumber ? "valid" : "invalid"}>
                    Numbers (0-9)
                  </li>
                </ul>
            </div>
            <div className="forgot-password-login w-100"></div>
            <button className="continueButton mt-4" type="submit">
              Reset password
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default ResetPasswordPage;
